import { useParams } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import { gql, useMutation, useQuery } from 'urql';

import { Locker, MarketingSettings } from '@/bits';
import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  ErrorMessage,
  InlineIconButton,
  Value,
} from '@/components';
import { RefreshIcon, TrashIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { PlayerRemoveEmailSuppressionsForm } from '@/forms';
import { Nullable } from '@/types';
import {
  ChangeMarketingBlockMutation,
  ChangeMarketingBlockMutationVariables,
  PlayerMarketingBlockQuery,
  PlayerMarketingBlockQueryVariables,
} from './__generated__/component';

const BLOCK_STATIC_QUERY = graphql`
  query SanityPlayerMarketingBlock {
    sanityPlayerMarketingBlock {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const query = gql`
  query PlayerMarketingBlock($playerId: ID!) {
    player(playerId: $playerId) {
      id
      marketingPreferences {
        marketingBlocked
      }
      isEmailMarketingAccepted
      isSmsMarketingAccepted
      isPhoneMarketingAccepted
      isWebPushMarketingAccepted
      suppressionGroups {
        id
        name
      }
    }
  }
`;

const changeMarketingBlockMutation = gql`
  mutation ChangeMarketingBlock($playerId: ID!, $isMarketingBlocked: Boolean!) {
    changeMarketingBlockSettings(
      playerId: $playerId
      isMarketingBlocked: $isMarketingBlocked
    ) {
      id
      marketingPreferences {
        marketingBlocked
      }
    }
  }
`;

const PlayerMarketingBlock = () => {
  const { playerId } = useParams();
  const { t } = useTranslate();
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

  const block =
    useStaticQuery<Queries.SanityPlayerMarketingBlockQuery>(
      BLOCK_STATIC_QUERY,
    ).sanityPlayerMarketingBlock;

  const [{ data, fetching }, refresh] = useQuery<
    PlayerMarketingBlockQuery,
    PlayerMarketingBlockQueryVariables
  >({
    query,
    variables: {
      playerId,
    },
  });

  const isMarketingBlocked = data?.player.marketingPreferences.marketingBlocked;
  const suppressionGroups = data?.player.suppressionGroups;

  const [changePlayerMarketingState, changePlayerMarketingBlock] = useMutation<
    ChangeMarketingBlockMutation,
    ChangeMarketingBlockMutationVariables
  >(changeMarketingBlockMutation);

  if (!block) {
    return null;
  }

  const submitMarketingBlock = () => {
    changePlayerMarketingBlock({
      playerId,
      isMarketingBlocked: !isMarketingBlocked,
    }).then((res) => {
      if (res.error?.message) {
        setErrorMessage(res.error.message);
      } else {
        refresh({ requestPolicy: 'network-only' });
      }
    });
  };

  const isEmailMarketingAccepted = data?.player.isEmailMarketingAccepted;
  const isSmsMarketingAccepted = data?.player.isSmsMarketingAccepted;
  const isPhoneMarketingAccepted = data?.player.isPhoneMarketingAccepted;
  const isWebPushMarketingAccepted = data?.player.isWebPushMarketingAccepted;

  const marketingSettings = {
    email: isEmailMarketingAccepted ?? null,
    sms: isSmsMarketingAccepted ?? null,
    phone: isPhoneMarketingAccepted ?? null,
    webPush: isWebPushMarketingAccepted ?? null,
  };

  return (
    <Card
      size="md"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={changePlayerMarketingState.fetching}
            className="flex"
            onClick={submitMarketingBlock}
          >
            <Locker isLocked={isMarketingBlocked || false} />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div
          className={`${
            isMarketingBlocked && 'pointer-events-none opacity-50'
          }`}
        >
          <MarketingSettings
            playerId={playerId}
            fetching={fetching}
            marketingSettings={marketingSettings}
          />
          <ErrorMessage message={errorMessage} />
        </div>
        <hr className="col-span-3" />
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          <Value
            title="Email Suppression Groups"
            value={suppressionGroups?.map((group) => group.name).join(', ')}
            fetching={fetching}
            suffix={
              suppressionGroups?.length ? (
                <ControlledModal
                  content={
                    <PlayerRemoveEmailSuppressionsForm playerId={playerId} />
                  }
                >
                  <InlineIconButton>
                    <TrashIcon />
                  </InlineIconButton>
                </ControlledModal>
              ) : null
            }
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerMarketingBlock;
